import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLastDefined } from '@/hooks/common/useLastDefined';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { IUserManagementTableRow, roleNamesAndDescriptions } from '@/types/admin/userManagement';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useErrorToast, useToast } from '@/contexts/admin/ToastContext';
import { IOrganization, IProperty } from '@/types/admin/navigation';
import { 
  UserAccountInfoSection,
  UserRoleSelectionSection,
  UserPreviewSection,
  CreateUserSuccessModal,
} from '@/components';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelTabs } from '@/components/common/admin/desktop/panel/DesktopPanelTabs';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { useEditUserForm } from '../CreateUser/hooks/useEditUserForm';
import { useCreateUserForm } from '../CreateUser/hooks/useCreateUserForm';
import { useCreateUserMutation, useAssignRolesMutation, useEditUserMutation, useRevokeRoleMutation, useDeleteUserMutation } from '@/store/api/admin/userManagementApi';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin';
import { showConfirm } from '@/utils/common/alert';

// Stable default user to satisfy unconditional hook calls in create mode
const DEFAULT_EDIT_USER: IUserManagementTableRow = {
  id: 0,
  name: '',
  userType: '',
  email: '',
  organization: [],
  properties: [],
};

interface DesktopUserManagementSidePanelProps {
  visible: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  user?: IUserManagementTableRow;
}

export const DesktopUserManagementSidePanel: React.FC<DesktopUserManagementSidePanelProps> = ({
  visible,
  onClose,
  mode,
  user: selectedUser,
}) => {
  const styles = useStyles();
  // The host keeps this panel mounted so it can slide in and out; hold the
  // last user for the slide-out in case the selection is cleared on close.
  const user = useLastDefined(selectedUser);
  const adminUser = useAdminUser();
  const { showApiError } = useErrorToast();
  const { showSuccess } = useToast();

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  // Mutations
  const [createUser, { isLoading: isCreateUserLoading }] = useCreateUserMutation();
  const [assignUserRoles, { isLoading: isAssignUserLoading }] = useAssignRolesMutation();
  const [editUser, { isLoading: isEditUserLoading }] = useEditUserMutation();
  const [revokeRole, { isLoading: isRevokeRoleLoading }] = useRevokeRoleMutation();
  const [deleteUser, { isLoading: isDeleteUserLoading }] = useDeleteUserMutation();

  const isLoading = isCreateUserLoading || isAssignUserLoading || isEditUserLoading || isRevokeRoleLoading || isDeleteUserLoading;

  // Forms (hooks must be called unconditionally; provide stable default user for edit form)
  const createForm = useCreateUserForm();
  const editForm = useEditUserForm(user ?? DEFAULT_EDIT_USER);

  // Every open starts fresh: a draft discarded on close must not resurface.
  const resetOnClose = useRef(() => {});
  resetOnClose.current = () => {
    createForm.resetForm();
    editForm.resetForm();
  };
  useEffect(() => {
    if (!visible) {
      resetOnClose.current();
      setConfirmCloseVisible(false);
    }
  }, [visible]);

  const formData = mode === 'create' ? createForm.formData : editForm.formData as any;
  const errors = mode === 'create' ? createForm.errors : editForm.errors as any;
  const activeStep = mode === 'create' ? createForm.activeStep : editForm.activeStep as number;
  const handleFieldChange = mode === 'create' ? createForm.handleFieldChange : editForm.handleFieldChange as any;
  const handleNext = mode === 'create' ? createForm.handleNext : editForm.handleNext as any;
  const handleBack = mode === 'create' ? createForm.handleBack : editForm.handleBack as any;
  const goToStep = mode === 'create' ? createForm.goToStep : editForm.goToStep as any;
  const validateForm = mode === 'create' ? createForm.validateForm : editForm.validateForm as (() => boolean);

  // Safe defaults to avoid undefined access during initial mount
  const safeFormData = formData || {
    email: '',
    firstName: '',
    lastName: '',
    roleName: '',
    organizations: [],
    properties: [],
  };

  const canRenderEdit = mode === 'edit' ? !!user : true;

  const tabs = ['General', mode === 'create' ? 'Assign Permissions' : 'Edit Permissions', 'Preview'];

  const handleSubmit = useCallback(async () => {
    if (!validateForm || !validateForm()) return;

    try {
      if (mode === 'create') {
        const createResponse = await createUser({
          email: createForm.formData.email,
          firstName: createForm.formData.firstName,
          lastName: createForm.formData.lastName,
        }).unwrap();

        if (createResponse.success) {
          const foundProperties = (adminUser.properties || []).filter((prop: IProperty) =>
            createForm.formData.properties?.some((selectedProp: string) => prop.marketingName === selectedProp)
          );
          const foundOrgs = (adminUser.organizations || []).filter((org: IOrganization) =>
            createForm.formData.organizations?.some((selectedOrg: string) => org.name === selectedOrg)
          );

          await assignUserRoles({
            email: createForm.formData.email,
            roleName: createForm.formData.roleName,
            propertyIds: foundProperties.map((prop: IProperty) => prop.id),
            orgIds: foundOrgs.map((org: IOrganization) => org.id),
          }).unwrap();

          setShowSuccessModal(true);
        } else {
          throw new Error('Failed to create user account');
        }
      } else if (mode === 'edit' && user && editForm) {
        const editResponse = await editUser({
          accountId: user.id,
          email: editForm.formData.email,
          firstName: editForm.formData.firstName,
          lastName: editForm.formData.lastName,
        }).unwrap();

        if (editResponse.success) {
          const foundProperties = (adminUser.properties || []).filter((prop: IProperty) =>
            editForm.formData.properties?.some((selectedProp: string) => prop.marketingName === selectedProp)
          );
          const foundOrgs = (adminUser.organizations || []).filter((org: IOrganization) =>
            editForm.formData.organizations?.some((selectedOrg: string) => org.name === selectedOrg)
          );

          const apiPayload: any = {
            email: editForm.formData.email,
            roleName: editForm.formData.roleName,
          };

          if (editForm.formData.roleName === roleNamesAndDescriptions.orgAdmin.value) {
            const orgIds = foundOrgs.map((org: IOrganization) => org.id);
            if (orgIds.length > 0) {
              apiPayload.orgIds = orgIds;
            } else {
              throw new Error('Organization Admin role requires at least one organization to be selected');
            }
          } else if ([
            roleNamesAndDescriptions.propertyAdmin.value,
            roleNamesAndDescriptions.billingAdmin.value,
          ].includes(editForm.formData.roleName)) {
            const propertyIds = foundProperties.map((prop: IProperty) => prop.id);
            if (propertyIds.length > 0) {
              apiPayload.propertyIds = propertyIds;
            } else {
              throw new Error('Property/Billing Admin role requires at least one property to be selected');
            }
          }

          // Always assign roles on edit to reflect org/property changes
          await assignUserRoles(apiPayload).unwrap();

          // Show success toast and close panel
          showSuccess('User updated successfully!');
          onClose();
        } else {
          throw new Error('Failed to update user account');
        }
      }
    } catch (error: any) {
      console.error('User submit error:', error);
      showApiError(error, mode === 'create' ? 'Failed to create user' : 'Failed to update user');
    }
  }, [mode, validateForm, createUser, assignUserRoles, editUser, adminUser, user, editForm, createForm, showApiError, showSuccess, onClose]);

  const handleDelete = useCallback(async () => {
    if (!user || !editForm) return;

    const firstConfirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.deleteUser.title,
      DESKTOP_CONFIRM_COPY.deleteUser.message,
      DESKTOP_CONFIRM_COPY.deleteUser.confirmText,
      DESKTOP_CONFIRM_COPY.deleteUser.cancelText,
    );
    if (!firstConfirmed) return;

    const finalConfirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.deleteUser.finalTitle,
      DESKTOP_CONFIRM_COPY.deleteUser.finalMessage,
      DESKTOP_CONFIRM_COPY.deleteUser.finalConfirmText,
      DESKTOP_CONFIRM_COPY.deleteUser.finalCancelText,
    );
    if (!finalConfirmed) return;

    try {
      if (user.userType && user.userType !== 'No Role') {
        const currentOrgs = (adminUser.organizations || []).filter((org: IOrganization) =>
          user.organization?.includes(org.name)
        );
        const currentProps = (adminUser.properties || []).filter((prop: IProperty) =>
          user.properties?.includes(prop.marketingName)
        );

        for (const org of currentOrgs) {
          await revokeRole({ email: user.email, organizationId: org.id, roleName: roleNamesAndDescriptions.orgAdmin.value }).unwrap();
        }
        for (const prop of currentProps) {
          // Role could be property or billing admin; use user.userType to send exact role
          await revokeRole({ email: user.email, propertyId: prop.id, roleName: user.userType }).unwrap();
        }
        if (user.userType === roleNamesAndDescriptions.superAdmin.value) {
          await revokeRole({ email: user.email, roleName: roleNamesAndDescriptions.superAdmin.value }).unwrap();
        }
      }

      await deleteUser({ accountId: user.id }).unwrap();
      
      // Show success toast
      showSuccess('User deleted successfully!');
      onClose();
    } catch (error: any) {
      console.error('Delete user error:', error);
      showApiError(error, 'Failed to delete user');
    }
  }, [user, editForm, adminUser, revokeRole, deleteUser, showApiError, showSuccess, onClose]);

  const handleSuccessClose = useCallback(() => {
    setShowSuccessModal(false);
    onClose();
  }, [onClose]);

  const attemptClose = useCallback(() => {
    setConfirmCloseVisible(true);
  }, []);

  const confirmDiscard = useCallback(() => {
    setConfirmCloseVisible(false);
    onClose();
  }, [onClose]);

  const cancelDiscard = useCallback(() => {
    setConfirmCloseVisible(false);
  }, []);

  const content = useMemo(() => {
    if (!canRenderEdit) return null;
    const step = activeStep ?? 0;
    const sectionProps: any = {
      formData: safeFormData,
      errors,
      onFieldChange: handleFieldChange,
      adminUser,
      isEditMode: mode === 'edit',
    };

    if (step === 0) return <UserAccountInfoSection {...sectionProps} />;
    if (step === 1) return <UserRoleSelectionSection {...sectionProps} />;
    return <UserPreviewSection formData={formData} />;
  }, [activeStep, formData, errors, handleFieldChange, adminUser, mode]);

  return (
    <DesktopSidePanel visible={visible} onClose={attemptClose} width={560}>
      <View style={styles.container}>
        <DesktopPanelHeader title={mode === 'create' ? 'Create User' : 'Edit User'} onClose={attemptClose} />

        {canRenderEdit && (
          <DesktopPanelTabs tabs={tabs} activeIndex={activeStep ?? 0} onChange={goToStep} />
        )}

        <DesktopPanelBody scroll>
          {content}
        </DesktopPanelBody>

        <View style={styles.actions}>
          {mode === 'edit' && canRenderEdit && (
            <TouchableOpacity style={[styles.button, styles.deleteButton]} onPress={handleDelete} disabled={isLoading}>
              <Text style={styles.deleteText}>Delete</Text>
            </TouchableOpacity>
          )}
          <View style={{ flex: 1 }} />
          <TouchableOpacity style={[styles.button, styles.secondaryButton]} onPress={attemptClose} disabled={isLoading}>
            <Text style={styles.secondaryText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.primaryButton]}
            onPress={((mode === 'create' ? activeStep : (activeStep ?? 0)) === 2) ? handleSubmit : handleNext}
            disabled={isLoading || (mode === 'edit' && !canRenderEdit)}
          >
            <Text style={styles.primaryText}>
              {((mode === 'create' ? activeStep : (activeStep ?? 0)) === 2)
                ? (mode === 'create' ? 'Create' : 'Update')
                : 'Next'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Success Modal */}
      <CreateUserSuccessModal
        visible={showSuccessModal}
        onClose={handleSuccessClose}
        userName={`${safeFormData.firstName ?? ''} ${safeFormData.lastName ?? ''}`}
      />
      <WebConfirmModal
        visible={confirmCloseVisible}
        title={mode === 'create' ? 'Discard New User?' : 'Discard Changes?'}
        message={mode === 'create' ? 'Are you sure you want to discard this new user?' : 'Are you sure you want to discard your changes?'}
        confirmText={mode === 'create' ? 'Discard' : 'Discard'}
        cancelText={'Keep Editing'}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </DesktopSidePanel>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors, semantic }) =>
    StyleSheet.create({
      container: {
        flex: 1,
        backgroundColor: 'transparent',
      },
      actions: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderTopWidth: 1,
        borderTopColor: components.admin.dataTable.border,
        gap: 8,
      },
      button: {
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 8,
      },
      primaryButton: {
        backgroundColor: PRIMITIVE_COLORS.resioBlue,
      },
      primaryText: {
        color: '#FFFFFF',
        fontWeight: '600',
      },
      secondaryButton: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: components.admin.dataTable.border,
      },
      secondaryText: {
        color: colors.text.primary,
        fontWeight: '600',
      },
      deleteButton: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: semantic.status.error,
      },
      deleteText: {
        color: semantic.status.error,
        fontWeight: '600',
      },
    }),
  );


